
import { of as observableOf, Observable, BehaviorSubject, of, forkJoin } from 'rxjs';

import { map, take } from 'rxjs/operators';
import * as jwtDecode from 'jwt-decode';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { superLocalStorage } from '~common/app/shared/utils/local-storage';
import { superSessionStorage } from '~common/app/shared/utils/session-storage';
import { initFreshchat } from '~common/app/shared/utils/freshchat';

import { DialogPasswordComponent } from 'app/shared/components/dialog-password/dialog-password.component';
import { ApiErrorInterceptorConfig } from 'app/shared/services/api-error.interceptor';
import { settings } from 'app/app.settings';
import { MatDialog } from '@angular/material/dialog';
import { FlashService } from '../../shared/components/flash/services/flash.service';

import tracking from '~common/app/shared/utils/tracking';

const API_PATH = settings.api.url;

@Injectable()
export class AuthService {
  key = 'auth.token';
  securityKey = 'auth.token.security';
  token$ = new BehaviorSubject<any>(false);
  loadedMetrics$ = new BehaviorSubject<any>({});
  authenticated$ = new BehaviorSubject<any>({});
  securityToken = superSessionStorage.get('security-token');

  constructor(
    private http: HttpClient,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public dialog: MatDialog,
    public flashService: FlashService
  ) {
    this.autologin();
    this.listenTokenInUrl();
  }

  autologin() {
    const token = this.getToken();

    this.setToken(token);
  }

  login(email: string, password: string, g_recaptcha: any, remember = false) {
    const lifetime = remember ? settings.api.lifetime : null;

    this.clearSession();

    return this.http.post(API_PATH + '/shift/auth/login', {
      email,
      password,
      g_recaptcha,
      lifetime,
    }).pipe(map(response => {
      const token = response['access_token'];

      this.setToken(token, remember);

      return of(token);
    }));
  }

  assessPasswordStrength(password) {
    return this.http.post(API_PATH + '/auth/strength', { password }).pipe(map(response => {
      if (response['password']) {
        this.flashService.warning('auth.login.weak-password', 10000);
      }
    }));
  }

  loginSso(email: string) {
    return this.http.post(API_PATH + '/auth/saml/sso', {
      email,
      app: 'shift'
    });
  }

  listenTokenInUrl() {
    this.activatedRoute.queryParams.subscribe((params: any) => {
      const token = params['token'];

      if (token) {
        this.setToken(token);
        this.router.navigate(['/']);
      }
    });
  }

  signup(email) {
    return this.http.post(API_PATH + '/users/merchants', {
      email
    });
  }

  refreshToken() {
    return this.http.post(API_PATH + '/auth/extend', {}).pipe(take(1)).subscribe(response => {
      const token = response['access_token'];

      this.setToken(token);

      return observableOf(token);
    });
  }

  clearSession() {
    superSessionStorage.clear();
    this.forgetRememberedToken();
    this.forgetSessionToken();
    this.token$.next(false);
    this.authenticated$.next(null);
  }

  logout() {
    this.http.delete(API_PATH + '/auth/logout').subscribe(() => {
      this.clearSession();
    })

    this.router.navigate(['/login']);
  }

  // Authentication

  isAuthenticated(): Observable<boolean> {
    const token$ = this.token$.asObservable();

    return token$.pipe(map((token) => {
      if (settings.app.SSOEnabled) {
        return true;
      }
      if (token) {
        return !this.isTokenExpired(token);
      }
      return false;
    }));
  }

  getAuthenticatedUser() {
    return this.authenticated$.asObservable();
  }

  setAuthenticatedUser(token) {
    try {
      const user = this.decodeToken(token)['user_claims'];
      this.authenticated$.next(user);
    } catch (error) { }
  }

  // Local Storage

  setRememberedToken(token: any) {
    return superLocalStorage.set(this.key, token);
  }

  getRememberedToken() {
    return superLocalStorage.get(this.key);
  }

  forgetRememberedToken() {
    return superLocalStorage.remove(this.key);
  }

  // Session Storage

  setSessionToken(token: any) {
    this.token$.next(token);
    return superSessionStorage.set(this.key, token);
  }

  getSessionToken() {
    return superSessionStorage.get(this.key);
  }

  forgetSessionToken() {
    return superSessionStorage.remove(this.key);
  }

  getToken() {
    const sessionToken = this.getSessionToken();
    const rememberedToken = this.getRememberedToken();
    const token = sessionToken || rememberedToken;

    if (token && this.isTokenExpired(token)) {
      if (this.isTokenExpired(token)) {
        console.log('token has expired', token);
      }

      this.forgetSessionToken();
      this.forgetRememberedToken();
      this.setAuthenticatedUser(null);
      return null;
    }
    return token;
  }

  setToken(token, remember = null) {
    if (remember === null) {
      remember = !!this.getRememberedToken();
    }

    if (remember) {
      this.setRememberedToken(token);
    } else {
      this.forgetRememberedToken();
    }

    this.setSessionToken(token);
    this.setAuthenticatedUser(token);
  }

  // JW

  decodeToken(token: string): Object {
    return jwtDecode(token);
  }

  getTokenExpirationDate(token: string): Date {
    const decoded: any = this.decodeToken(token);

    if (!decoded.hasOwnProperty('exp')) {
      return null;
    }

    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);

    return date;
  }

  isTokenExpired(token: string, offsetSeconds?: number): boolean {
    const date = this.getTokenExpirationDate(token);

    offsetSeconds = offsetSeconds || 0;

    if (date === null) {
      return false;
    }

    return !(date.valueOf() > (new Date().valueOf() + (offsetSeconds * 1000)));
  }

  // Update

  updatePassword(old_password: string, new_password: string) {
    return this.http.put(API_PATH + '/users/me/change-password', {
      old_password,
      new_password,
      interceptorConfig: <ApiErrorInterceptorConfig>{
        skipErrorStatuses: [400],
      }
    });
  }

  get(): Observable<any> {
    return this.http.get(API_PATH + '/users/me');
  }

  getProgram(): Observable<any> {
    return this.http.get(API_PATH + '/programs/me');
  }

  ifHasDelegation(ifCallback = null, elseCallback = null) {
    this.getAuthenticatedUser().pipe(take(1)).subscribe((response: any) => {
      if (!response || !response.merchant) {
        return;
      }

      if (response.merchant.has_delegation && typeof ifCallback === 'function') {
        return ifCallback();
      }

      if (!response.merchant.has_delegation && typeof elseCallback === 'function') {
        return elseCallback();
      }
    });
  }

  ifHasCoupon(ifCallback = null, elseCallback = null) {
    this.getAuthenticatedUser().pipe(take(1)).subscribe((response: any) => {
      if (!response || !response.program) {
        return;
      }

      if (response.program.configuration?.has_coupon && typeof ifCallback === 'function') {
        return ifCallback();
      }

      if (!response.program.configuration?.has_coupon && typeof elseCallback === 'function') {
        return elseCallback();
      }
    });
  }

  hasScope(scope) {
    const scopes = this.authenticated$.value?.scopes;

    if (scopes) {
      if (Array.isArray(scope)) {
        for (let i = 0; i < scope.length; i++) {
          if (scopes.includes(scope[i])) {
            return true;
          }
        }
      } else {
        return scopes.includes(scope);
      }
    }
    return false;
  }

  loadAuthenticated() {
    const user = this.get();
    const program = this.getProgram();

    forkJoin([user, program])
      .pipe(
        take(1)
      )
      .subscribe((responses: any) => {
        const response = responses[0];
        response.program = responses[1];
        this.authenticated$.next(response);

        // Piano Analytics
        tracking.setUser(response.program.id, response.program.name, true)
        // Freshchat
        initFreshchat('103000001254', 'shift', response);
      });
  }

  getAuthenticatedStatus() {
    return this.authenticated$.pipe(map((authenticated) => {
      if (authenticated && authenticated.merchant) {
        const merchant = authenticated.merchant;

        if (merchant.status === 'VALIDATED'
          && merchant.has_valid_wallet_banking_alias
        ) {
          return 'VALIDATED';
        }
        return 'NOT_VALIDATED';
      }
      return null;
    }));
  }

  update(data: Object) {
    return this.http.put(API_PATH + '/users/me', data);
  }

  // Security token

  getSecurityToken() {
    return superSessionStorage.get(this.securityKey);
  }

  setSecurityToken(token: string) {
    this.securityToken = token;
    superSessionStorage.set(this.securityKey, token);
  }

  resetSecurityToken() {
    this.securityToken = null;
    superSessionStorage.set(this.securityKey, null);
  }

  loginForSecurityToken(password: string) {
    return this.http.post(API_PATH + '/auth/token', {
      password,
      interceptorConfig: <ApiErrorInterceptorConfig><unknown>{
        skipErrorStatuses: [403, 401]
      }
    }).pipe(map(response => {
      const token = response['access_token'];

      if (token) {
        this.setSecurityToken(token);
      }
    }));
  }

  openLoginForSecurityToken(callback: Function) {
    return this.dialog.open(DialogPasswordComponent, {
      width: '400px',
      data: {
        authService: this
      }
    }).afterClosed().subscribe((confirm) => {
      if (confirm) {
        if (callback) {
          callback();
        }
      }
    });
  }

  // Metrics

  loadMetrics() {
    return this.http.get(API_PATH + '/users/me/counters').subscribe((response) => {
      this.loadedMetrics$.next(response);
    });
  }

  getLoadedMetrics(): Observable<any> {
    return this.loadedMetrics$.asObservable();
  }

  // Subscriptions

  // /users/me/mail/subscriptions
  getSubscriptions(): Observable<any> {
    return this.http.get(settings.api.url + '/users/me/mail/subscriptions').pipe(map(response => {
      return response
    }));
  }

  addSubscription(type: string) {
    return this.http.post(settings.api.url + '/users/me/mail/subscriptions/' + type, {});
  }

  removeSubscription(type: string) {
    return this.http.delete(settings.api.url + '/users/me/mail/subscriptions/' + type);
  }
}
